import { csvCell } from '~/utils/csvCell';
import { type LensDataReadiness, lensDataReady } from '~/utils/leLensTransport';

import { luxMetrics, number } from '../CustomerComms/liveModel';

import schemas from './cardSchema.json';
import { type LuxScope, countryLabel, scopeProblem } from './filterBindings';
import type {
  Column,
  FidelityDashboard,
  FidelityFilters,
  FidelityPanel,
  FidelityRow,
  FidelitySeries,
  FidelityViewId,
  Metric,
  MetricFormat,
} from './presentationModel';
import { FIDELITY_COLORS as C, FIDELITY_VIEWS } from './presentationModel';

export const panelForView = (view: FidelityViewId) =>
  view === 'overview' ? 'executive' : view.startsWith('takeup') || view === 'margin' ? 'opportunities' : view;
const W = (name: string) => 'workbook_' + name;
export function activeCards(view: FidelityViewId): Array<string> {
  if (view === 'overview') {
    return [
      W('executive_country'),
      'executive_current_members',
      'executive_sales_summary',
      'executive_sales_timeline',
      'executive_takeup',
      'executive_membership_windows',
    ];
  }
  if (view === 'margin') {
    return [
      'opportunities_summary',
      'opportunities_rows',
      W('opportunity_timeline'),
      W('opportunity_country'),
      'opportunities_breakdown',
    ];
  }
  if (view.startsWith('takeup')) {
    return [
      'opportunities_summary',
      'opportunities_rows',
      W('opportunity_timeline'),
      W('opportunity_country'),
      'opportunities_breakdown',
      ...(view === 'takeup-trend'
        ? [W('opportunity_country_trend'), W('opportunity_weekly_country')]
        : [
            W('opportunity_vendor'),
            'opportunities_price',
            ...(view === 'takeup' ? [W('opportunity_channel'), W('opportunity_band'), W('opportunity_max_band')] : []),
          ]),
    ];
  }
  if (view === 'renewals') {
    return [
      'renewals_summary',
      W('renewal_status'),
      W('renewal_rates'),
      W('renewal_detail'),
      W('renewal_weekly'),
      'renewals_savings_band',
      'renewals_margin_band',
      'renewals_ttv_band',
      'renewals_customers',
    ];
  }
  if (view === 'cancellations') {
    return [
      'cancellations_summary',
      W('cancellation_trend'),
      W('cancellation_days'),
      W('cancellation_detail'),
      'cancellations_savings_band',
      'cancellations_margin_band',
      'cancellations_ttv_band',
      'cancellations_customers',
    ];
  }
  return [W('cohort_cells')];
}
const ratio = (a: unknown, b: unknown) => {
  const n = number(a),
    d = number(b);
  return n === null || d === null || d <= 0 ? null : n / d;
};

/** Optional card from svc-reporting that estimates Auto-Renew On over renewals due from today to month end,
 * independent of the report window (Tableau's "Estimated Renewal Rate"). Requested separately so the page still
 * loads where the backend doesn't serve it yet. */
export const AUTO_RENEW_ON_CARD = 'renewals_auto_renew_on';
export function autoRenewOnRate(
  cards: ReadonlyArray<{ cardId: string; status: string; rows?: Array<Record<string, unknown>> }> | undefined,
) {
  const card = cards?.find((c) => c.cardId === AUTO_RENEW_ON_CARD);
  const row = card?.status === 'ready' ? card.rows?.[0] : undefined;
  return row ? ratio(row.upcoming_month_likely, row.upcoming_month_base) : null;
}
const plus = (...values: Array<unknown>) => {
  const n = values.map(number);
  return n.some((v) => v === null) ? null : n.reduce((a, b) => a + b, 0);
};
const text = (v: unknown): string => {
  if (v === null || v === undefined) {
    return 'Unknown';
  }
  if (typeof v === 'object' && 'value' in v) {
    return text(v.value);
  }
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
    return String(v);
  }
  throw new Error('Invalid source dimension.');
};
const clean = (row: Record<string, unknown>): FidelityRow =>
  Object.fromEntries(Object.entries(row).map(([k, v]) => [k, v == null ? null : typeof v === 'number' ? v : text(v)]));
const metric = (
  key: string,
  label: string,
  value: unknown,
  format: MetricFormat = 'number',
  definition = 'Independent source-card aggregate in the selected scope.',
): Metric => ({ key, label, value: number(value), format, definition });
const series = (
  key: string,
  label: string,
  color: string,
  format: MetricFormat = 'number',
  extras: Partial<FidelitySeries> = {},
): FidelitySeries => ({ key, label, color, format, ...extras });
const columns = (values: Array<[string, string, MetricFormat?]>): Array<Column> =>
  values.map(([key, label, format]) => ({ key, label, format }));
const panel = (
  id: string,
  title: string,
  kind: FidelityPanel['kind'],
  source: string,
  extras: Partial<FidelityPanel> = {},
): FidelityPanel => ({ id, title, kind, source, rows: [], columns: [], series: [], span: 'full', ...extras });
const mPanel = (id: string, title: string, source: string, metrics: Array<Metric>, sheets: Array<string> = []) =>
  panel(id, title, 'metrics', source, { metrics, originalSheets: sheets });
const bandSort = (a: string, b: string) => {
  const f = (s: string) => {
    const m = s.replace(/[$,]/g, '').match(/^-?\d+/);
    return m ? Number(m[0]) : Infinity;
  };
  return f(a) - f(b) || a.localeCompare(b);
};
const takeup = (r: FidelityRow): FidelityRow => ({
  ...r,
  label:
    r.vendor_name ??
    (r.country_code_2 == null ? undefined : countryLabel(text(r.country_code_2))) ??
    r.price_axis ??
    r.order_price_band ??
    r.max_item_price_band ??
    r.traffic_channel ??
    'Unknown',
  date: r.bucket ?? r.week_bucket ?? r.date ?? null,
  market: r.country_code_2 == null ? null : countryLabel(text(r.country_code_2)),
  takeups: r.takeups,
  eligibleOrders: r.eligible_orders,
  takeup: ratio(r.takeups, r.eligible_orders),
  revenue: r.subscription_revenue,
  attached: ratio(r.takeups, r.eligible_orders),
  notAttached: ratio(
    number(r.eligible_orders) === null || number(r.takeups) === null
      ? null
      : number(r.eligible_orders) - number(r.takeups),
    r.eligible_orders,
  ),
});
const renewal = (r: FidelityRow): FidelityRow => ({
  ...r,
  date: r.bucket ?? r.week_bucket ?? r.date,
  actual: ratio(r.renewed, r.decided),
  estimated: ratio(r.future_likely, r.future_base),
});
const margin = (r: FidelityRow): FidelityRow => ({
  ...r,
  date: r.bucket,
  market: countryLabel(text(r.country_code_2)),
  gmi: plus(r.added_margin, r.lost_margin),
  attributed: r.attributed_fees,
  total: plus(r.added_margin, r.lost_margin, number(r.attributed_fees) ?? 0),
  added: r.added_margin,
  lost: r.lost_margin,
  packages: r.eligible_packages,
});
const takeupColumns = columns([
  ['label', 'Split'],
  ['takeups', 'New-member eligible orders', 'number'],
  ['eligibleOrders', 'Eligible orders', 'number'],
  ['takeup', 'Take-up rate', 'percent'],
  ['revenue', 'LP+ subscription revenue', 'money'],
]);
const statusColors: Record<string, string> = {
  Renewed: '#4e79a7',
  'Renewed Outside Renewal Window': '#76b7b2',
  'Active - Renewal Likely': '#79aacf',
  'Renewed then Cancelled outside Grace Period': '#8ed07f',
  'Renewed then Cancelled within Grace Period': '#b3e0a6',
  Lapsed: '#e15759',
  'Awaiting Renewal - Payment Failure': '#e36420',
  'Awaiting Renewal - Auto Renew Off': '#f5a645',
  'Active - Renewal Unlikely': '#feb4a6',
  '(Null)': '#bab0ac',
};
const countryColors: Record<string, string> = {
  Australia: '#4e79a7',
  'New Zealand': '#d7b5a6',
  Singapore: '#e15759',
  'United States': '#79706e',
  'United Kingdom': '#f1ce63',
  Canada: '#f28e2b',
  'Hong Kong': '#499894',
  India: '#79706e',
  Indonesia: '#86bcb6',
};
const categoryColors = [
  '#4e79a7',
  '#f28e2b',
  '#76b7b2',
  '#e15759',
  '#59a14f',
  '#b07aa1',
  '#edc949',
  '#9c755f',
  '#ff9da7',
  '#bab0ac',
];
/** Pivot independently computed cells; repeated source keys are rejected before this step. */
function pivot(
  rows: Array<FidelityRow>,
  x: string,
  group: string,
  value: string,
): { rows: Array<FidelityRow>; names: Array<string> } {
  const names = [...new Set(rows.map((r) => text(r[group])))].sort(),
    index = new Map<string, FidelityRow>();
  for (const row of rows) {
    const key = text(row[x]),
      g = names.indexOf(text(row[group])),
      r = index.get(key) ?? { date: key, label: key };
    r['s' + g] = row[value];
    index.set(key, r);
  }
  return { rows: [...index].sort(([a], [b]) => a.localeCompare(b)).map(([, r]) => r), names };
}
export type SettledLux = {
  ready: boolean;
  problem: string | null;
  identity: string;
  scope: LuxScope;
  filters: FidelityFilters;
  view: FidelityViewId;
  board: FidelityDashboard;
  cards: Record<string, Array<FidelityRow>>;
};
export const luxIdentity = (filters: FidelityFilters, view: FidelityViewId, scope: LuxScope, marketSplit = true) =>
  JSON.stringify([filters, view, scope, marketSplit]);
export function adaptLux(
  filters: FidelityFilters,
  view: FidelityViewId,
  scope: LuxScope,
  input: LensDataReadiness,
  marketSplit = true,
  constraint: string | null = null,
  autoRenewOn: number | null = null,
): SettledLux {
  const source = panelForView(view),
    board: FidelityDashboard = {
      id: view,
      title: FIDELITY_VIEWS.find((v) => v.id === view).label,
      subtitle: '',
      source,
      panels: [],
      notes: [
        'Distinct counts and medians computed at each source card grain. No summation of grouped distincts, ratios or medians.',
      ],
      missingFeatures: [],
      originalSheets: [],
    };
  const result: SettledLux = {
    ready: false,
    problem: null,
    identity: luxIdentity(filters, view, scope, marketSplit),
    scope,
    filters,
    view,
    board,
    cards: {},
  };
  const problem = constraint ?? scopeProblem(filters, view, scope);
  if (problem) {
    result.problem = problem;
    return result;
  }
  if (!lensDataReady(input, activeCards(view))) {
    result.problem = 'Waiting for complete live source cards for the current scope.';
    return result;
  }
  try {
    for (const id of activeCards(view)) {
      const card = input.cards.find((c) => c.cardId === id),
        schema = schemas.find((c) => c.id === id);
      if (!schema) {
        throw new Error('Missing typed card contract ' + id);
      }
      const rows = card.rows ?? [];
      if ((card.status === 'ready' && !rows.length) || (card.status === 'empty' && rows.length)) {
        throw new Error('Contradictory ' + id + ' population.');
      }
      const seen = new Set<string>();
      result.cards[id] = rows.map((row) => {
        for (const key of [...schema.keys, ...schema.metrics]) {
          if (!(key in row)) {
            throw new Error('Missing ' + id + '.' + key);
          }
        }
        const key = JSON.stringify(schema.keys.map((k) => text(row[k])));
        if (seen.has(key)) {
          throw new Error('Duplicate ' + id + ' grain.');
        }
        seen.add(key);
        const cleaned = clean(row);
        for (const key of schema.metrics) {
          const n = number(row[key]);
          if (
            (id === 'executive_membership_windows' || id === 'executive_current_members' ||
              id === 'executive_auto_renew_off' ||
              id === 'executive_takeup' ||
              id === 'executive_new_subscriptions') &&
            row[key] !== null &&
            (typeof row[key] !== 'number' || !Number.isSafeInteger(row[key]) || row[key] < 0)
          ) {
            throw new Error('Invalid member source count: ' + id + '.' + key);
          }
          if (row[key] !== null && n === null) {
            throw new Error('Invalid ' + id + '.' + key);
          }
          cleaned[key] = n;
        }
        return clean(luxMetrics(cleaned));
      });
    }
    const rows = (id: string) => result.cards[id] ?? [],
      first = (id: string) => rows(id)[0] ?? {};
    const base = panelForView(view),
      s = first(base + '_summary');
    if (view === 'overview') {
      const stockRows = rows('executive_current_members');
      if (stockRows.length > 1) throw new Error('Current-member stock must have one as-of row.');
      const stock = stockRows[0];
      const count = (value: unknown): number | null => {
        if (value === null || value === undefined) return null;
        const n = number(value);
        if (n === null || !Number.isSafeInteger(n) || n < 0) throw new Error('Invalid member source count.');
        return n;
      };
      const stockSourceRows = count(stock?.source_row_count);
      const activeMembers = stockSourceRows !== null && stockSourceRows > 0 ? count(stock?.active_members) : null;
      if (activeMembers !== null && activeMembers > stockSourceRows)
        throw new Error('Active-member count exceeds its source population.');
      const stockAsOf =
        typeof stock?.as_of_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(stock.as_of_date)
          ? stock.as_of_date
          : 'Unknown';
      board.notes.push(
        `Financial headlines and timelines use the workbook Sales Model; the country table retains its independent regional source. Current active members: all countries, stock as of ${stockAsOf} in Sydney, independent of selected dates and filters. Membership flows use independent All time (since 1 January 2024) and Yesterday windows across all countries; date and market selections affect the financial charts and country table. Auto-renew off counts cancellation-date rows, not membership terminations. Renewals and churn count distinct source customers/subscriptions; promo members and active auto-renew-off members are subsets.`,
      );
      if (activeMembers === null)
        board.missingFeatures.push(
          'Current active members Unknown: the stock source is empty, unconfigured or did not supply a verified count.',
        );
      const membershipRows = rows('executive_membership_windows');
      const allTime = membershipRows.find((row) => row.window_id === 'all_time');
      const yesterday = membershipRows.find((row) => row.window_id === 'yesterday');
      if (membershipRows.length !== 2 || !allTime || !yesterday) throw new Error('Missing membership windows.');
      for (const row of membershipRows) {
        if (Number(row.new_subscriptions_promo) > Number(row.new_subscriptions) ||
            Number(row.renewed_subscriptions_promo) > Number(row.renewed_subscriptions) ||
            Number(row.auto_renew_off_active) > Number(row.auto_renew_off) ||
            Number(row.churned_members_promo) > Number(row.churned_members)) {
          throw new Error('A membership subset exceeds its source population.');
        }
      }
      const takeupSource = first('executive_takeup');
      if (
        number(takeupSource.new_member_orders) !== null &&
        number(takeupSource.all_orders) !== null &&
        Number(takeupSource.new_member_orders) > Number(takeupSource.all_orders)
      )
        throw new Error('New-member opportunity orders exceed the source population.');
      const headline = first('executive_sales_summary');
      const headlineTimeline = new Map<string, FidelityRow>();
      for (const row of rows('executive_sales_timeline')) {
        const bucket = text(row.bucket),
          existing = headlineTimeline.get(bucket);
        if (!existing) headlineTimeline.set(bucket, { ...row });
        else
          for (const field of schemas.find((schema) => schema.id === 'executive_sales_timeline').metrics)
            existing[field] = plus(existing[field], row[field]);
      }
      const financial = [
        ['impact', 'Overall margin impact', 'overall_margin_impact', C.executiveTotal],
        ['subscription', 'New subscription margin', 'lp_subscription_total_new_members', C.executiveNewFees],
        ['renewedRevenue', 'Renewed recurring fees', 'lp_renewed_member_recurring_fee', C.executiveRenewedFees],
        ['lostMargin', 'Lost margin', 'lp_lost_margin_aud', C.lost],
        ['addedMargin', 'Added margin', 'lp_added_margin', C.added],
      ];
      for (const [key, label, field, color] of financial) {
        board.panels.push(
          panel('executive-' + key, label, 'line', source, {
            metrics: [metric(key, label, headline[field], 'money')],
            // Each chart plots the same measure as its headline, so the overall chart sums to the tile (Tableau parity).
            rows: [...headlineTimeline.values()].map((r) => ({ date: r.bucket, [key]: r[field] })),
            series: [series(key, label, color, 'money')],
            span: 'fifth',
            height: 220,
          }),
        );
      }
      board.panels.push(
        mPanel('members-today', 'Members · Today', source, [
          metric('activeMembers', 'Current active members (all countries)', activeMembers, 'number',
            `COUNTD customer with full UTC-naive start_date <= midnight on the Sydney business date < full UTC-naive end_date. Stock as of ${stockAsOf}; independent of selected dates and filters.`),
        ]),
        ...([['all_time', 'All time'], ['yesterday', 'Yesterday']] as const).map(([id, label]) => {
          const row = id === 'all_time' ? allTime : yesterday;
          return mPanel('members-' + id, 'Memberships · ' + label, source, [
            metric('newMembers', 'New subscriptions (all countries)', row.new_subscriptions, 'number',
              'COUNTD Sales Model customer with luxury_plus membership order, member flag true and renewal flag false. Transaction dates in the window before Sydney today; all countries and products, fixed awaiting_dates/completed item statuses, independent of dashboard slice filters.'),
            metric('newMembersPromo', 'New subscriptions (promo subset, all countries)', row.new_subscriptions_promo, 'number',
              'Distinct customers from the same new-subscription population with a non-null promo code; already included in new subscriptions.'),
            metric('returningMembers', 'Renewals (all countries)', row.renewed_subscriptions),
            metric('returningMembersPromo', 'Renewals (promo subset, all countries)', row.renewed_subscriptions_promo),
            metric('cancelled', 'Auto-renew off (all countries)', row.auto_renew_off, 'number',
              'COUNT of raw cancellation-date rows in the window, all statuses and countries; not membership terminations.'),
            metric('autoRenewOffActive', 'Auto-renew off (active subset, all countries)', row.auto_renew_off_active, 'number',
              'COUNT of the same cancellation-date rows whose luxplus_status is ACTIVE; all countries.'),
            metric('churned', 'Churned members (all countries)', row.churned_members),
            metric('churnedPromo', 'Churned members (promo subset, all countries)', row.churned_members_promo),
          ]);
        }),
        mPanel('health', 'Membership health', source, [
          metric(
            'takeup',
            'Eligible executive take-up',
            ratio(first('executive_takeup').new_member_orders, first('executive_takeup').all_orders),
            'percent',
            'COUNTD eligible numerator-flag opportunity orders (excluding luxury_plus standalone) / COUNTD eligible denominator-flag orders; excludes call-back and standalone membership orders. Selected transaction-date period, Luxury Escapes brand, fixed awaiting_dates/completed statuses; country and product filters apply.',
          ),
          metric('churnAllTime', 'Churn rate (All time, all countries)', ratio(allTime.churned_members, allTime.churn_denominator), 'percent'),
          metric('churnYesterday', 'Churn rate (Yesterday, all countries)', ratio(yesterday.churned_members, yesterday.churn_denominator), 'percent'),
        ]),
      );
      board.panels.push(
        panel('country', 'Executive summary by country', 'table', source, {
          rows: rows(W('executive_country')).map((r) => ({
            market: r.executive_country,
            ...Object.fromEntries(financial.map(([key, , field]) => [key, r[field]])),
            takeup: r.executive_takeup,
            churn: r.executive_churn,
          })),
          columns: [
            { key: 'market', label: 'Country' },
            ...financial.map(([key, label]) => ({ key, label, format: 'money' as const })),
            ...columns([
              ['takeup', 'Executive take-up', 'percent'],
              ['churn', 'Churn', 'percent'],
            ]),
          ],
        }),
      );
    } else if (view.startsWith('takeup')) {
      const timeline = rows(W('opportunity_timeline')).map(takeup),
        country = rows(W('opportunity_country')).map(takeup),
        vendor = rows(W('opportunity_vendor')).map(takeup),
        price = rows('opportunities_price')
          .map(takeup)
          .sort((a, b) => bandSort(text(a.label), text(b.label)));
      if (view !== 'takeup-trend') {
        board.panels.push(
          mPanel('takeup-kpis', 'Take-up', source, [
            metric('takeup', 'Eligible Take Up Rate %', s.takeup_rate, 'percent'),
            ...(view === 'takeup'
              ? [
                  metric(
                    'medianSavings',
                    'LP+ Median Savings per Order',
                    s.median_order_savings,
                    'money',
                    'Exact source-row median after the existing context/FIXED stages; never median of grouped medians.',
                  ),
                ]
              : []),
            metric('packagesPerOrder', 'LP+ Packages per Order', s.takeup_packages_per_order, 'decimal'),
          ]),
        );
      }
      if (view === 'takeup') {
        const bands = rows(W(filters.priceBandBasis === 'Max item price' ? 'opportunity_max_band' : 'opportunity_band'))
          .map(takeup)
          .sort((a, b) => bandSort(text(a.label), text(b.label)));
        board.panels.push(
          panel('takeup-revenue', 'LP+ subscription revenue', 'line', source, {
            rows: timeline,
            series: [series('revenue', 'LP+ subscription revenue', '#3d56b3', 'money')],
            span: 'third',
            originalSheets: ['Chart-all'],
          }),
          panel('price-band', 'LP+ subscription revenue by price band', 'bar', source, {
            rows: bands,
            series: [series('revenue', 'LP+ subscription revenue', '#3d56b3', 'money')],
            horizontal: true,
            span: 'third',
            originalSheets: ['price band order'],
          }),
          panel('traffic-channel', 'LP+ subscription revenue by traffic channel', 'bar', source, {
            rows: rows(W('opportunity_channel'))
              .map(takeup)
              .sort((a, b) => (number(b.revenue) ?? 0) - (number(a.revenue) ?? 0)),
            series: [series('revenue', 'LP+ subscription revenue', '#3d56b3', 'money')],
            horizontal: true,
            span: 'third',
            originalSheets: ['Traffic Channel'],
          }),
          panel('takeup-detail', 'Take-up detail by vendor', 'table', source, {
            rows: vendor,
            columns: [{ key: 'label', label: 'Vendor' }, ...takeupColumns.slice(1)],
            originalSheets: ['LP+ Take Up Rate -Table'],
          }),
        );
      } else if (view === 'takeup-overview') {
        board.panels.push(
          panel('price-band', 'Take-up by price / savings axis', 'bar', source, {
            rows: price,
            series: [
              series('notAttached', 'Not attached', '#4e79a7', 'percent'),
              series('attached', 'Attached', '#f28e2b', 'percent'),
            ],
            stacked: true,
            stackMode: 'percent',
            span: 'half',
            originalSheets: ['00 - TakeUpRate - Price Band - Chart'],
            note: 'Exact existing takeup_price_mode source CASE; title follows selected axis in source controls.',
          }),
          panel('takeup-vendors', 'Take-up by vendor', 'table', source, {
            rows: vendor,
            columns: [{ key: 'label', label: 'Vendor' }, ...takeupColumns.slice(1)],
            span: 'half',
            originalSheets: ['00 - TakeUpRate - Price Band - Table'],
          }),
        );
      } else {
        const grouped = pivot(rows(W('opportunity_country_trend')).map(takeup), 'date', 'country_code_2', 'takeup'),
          weekly = pivot(rows(W('opportunity_weekly_country')).map(takeup), 'date', 'country_code_2', 'takeup');
        board.panels.push(
          panel('takeup-counts', 'Eligible orders and new-member orders', 'bar', source, {
            rows: timeline,
            series: [
              series('eligibleOrders', 'Eligible orders', '#4e79a7'),
              series('takeups', 'New-member eligible orders', '#f28e2b'),
            ],
            span: 'half',
            stacked: true,
            originalSheets: ['TUR bar chart'],
          }),
          panel('takeup-rate', 'Take-up rate', 'line', source, {
            rows: marketSplit ? grouped.rows : timeline,
            series: marketSplit
              ? grouped.names.map((name, i) =>
                  series(
                    's' + i,
                    countryLabel(name),
                    countryColors[countryLabel(name)] ?? categoryColors[i % categoryColors.length],
                    'percent',
                    {
                      dashed: !countryColors[countryLabel(name)],
                    },
                  ),
                )
              : [series('takeup', 'All selected orders', '#4e79a7', 'percent')],
            span: 'half',
            originalSheets: ['TUR line chart'],
          }),
          panel('takeup-breakdown', 'Weekly country take-up breakdown', 'table', source, {
            rows: weekly.rows,
            columns: [
              { key: 'date', label: 'Week commencing' },
              ...weekly.names.map((name, i) => ({
                key: 's' + i,
                label: countryLabel(name),
                format: 'percent' as const,
              })),
            ],
            span: 'half',
            originalSheets: ['TUR breakdown'],
          }),
        );
      }
      board.panels.push(
        panel(
          'takeup-summary',
          view === 'takeup-trend' ? 'Take-up summary by country' : 'Country control totals',
          'table',
          source,
          {
            rows: country,
            columns: [{ key: 'label', label: 'Country' }, ...takeupColumns.slice(1)],
            span: view === 'takeup-trend' ? 'third' : 'full',
            originalSheets: ['TUR summary'],
          },
        ),
      );
      if (view === 'takeup') {
        board.panels.push(
          panel('takeup-source-price', 'Take-up by price / savings axis', 'composed', source, {
            rows: price,
            columns: takeupColumns,
            series: [
              series('eligibleOrders', 'Eligible orders', C.all, 'number', { type: 'bar' }),
              series('takeups', 'New-member eligible orders', C.attached, 'number', { type: 'bar' }),
              series('takeup', 'Take-up rate', C.all, 'percent', { type: 'line', axis: 'right' }),
            ],
            originalSheets: ['00 - TakeUpRate - Price Band - Chart'],
            note: 'Recovered source FIXED axis and independent COUNTD, not a substitute for the pinned revenue-by-band slot.',
          }),
        );
      }
    } else if (view === 'margin') {
      board.panels.push(
        mPanel('margin-kpis', 'Offer margin impact', source, [
          metric('attributed', 'Attributed subscription margin', s.attributed_fees, 'money'),
          metric('gmi', 'Gross margin impact', s.gross_margin_impact, 'money'),
          metric('total', 'Total margin impact', s.total_margin_impact, 'money'),
          metric('eligibleGrossMargin', 'Gross margin', s.eligible_margin, 'money'),
          metric('impactPerPackage', 'Offer GMI / package', s.margin_impact_per_package, 'money'),
          metric('memberPackageShare', 'LP+ % packages', s.member_package_share, 'percent'),
          metric('marginPercent', '% margin', ratio(s.eligible_margin, s.eligible_ttv), 'percent'),
        ]),
      );
      board.panels.push(
        panel('margin-chart', 'Margin impact', 'composed', source, {
          rows: rows(W('opportunity_timeline')).map(margin),
          series: [
            series('added', 'Added margin', C.added, 'money', { type: 'bar' }),
            series('lost', 'Lost margin', C.lost, 'money', { type: 'bar' }),
            series('gmi', 'Gross margin impact', C.offerGmi, 'money', { type: 'line' }),
            series('attributed', 'Attributed subscription margin', C.subscription, 'money', { type: 'line' }),
            series('total', 'Total margin impact', C.offerTotal, 'money', { type: 'line' }),
          ],
          height: 320,
          originalSheets: ['01-Margin-Chart'],
        }),
        panel('margin-table', 'Margin detail by country', 'table', source, {
          rows: rows(W('opportunity_country')).map(margin),
          columns: columns([
            ['market', 'Country'],
            ['gmi', 'Gross margin impact', 'money'],
            ['attributed', 'Attributed subscription margin', 'money'],
            ['total', 'Total margin impact', 'money'],
            ['packages', 'Packages', 'number'],
          ]),
          originalSheets: ['01-Margin-Table-Custom'],
        }),
      );
    } else if (view === 'renewals') {
      board.panels.push(
        mPanel('renewal-kpis', 'Renewals', source, [
          metric('estimated', 'Auto-Renew On', autoRenewOn ?? s.estimated_renewal_rate, 'percent'),
          metric('rate', 'Overall Renewal Rate', s.renewal_rate, 'percent'),
          metric('year1', '1st Year Renewal Rate', s.first_renewal_rate, 'percent'),
          metric('year2', '2nd Year Renewal Rate', s.second_renewal_rate, 'percent'),
          metric('averageOrders', 'Average No. Of Orders', s.average_orders, 'decimal'),
          metric('averageSavings', 'Average LP+ Savings', s.average_savings, 'money'),
        ]),
      );
      const periods = [...new Set(rows(W('renewal_status')).map((r) => text(r.subscription_period_number)))].sort();
      for (const period of periods) {
        const status = pivot(
          rows(W('renewal_status')).filter((r) => text(r.subscription_period_number) === period),
          'bucket',
          'renewal_status',
          'parent_subscriptions',
        );
        board.panels.push(
          panel('renewal-status-' + period, 'Renewed · subscription period ' + period, 'bar', source, {
            rows: status.rows,
            series: status.names.map((name, i) => series('s' + i, name, statusColors[name] ?? '#bab0ac')),
            stacked: true,
            stackMode: 'count',
            span: 'half',
            facet: 'Subscription period ' + period,
            originalSheets: ['Renewed'],
          }),
          panel('renewal-rates-' + period, 'Renewal rate · subscription period ' + period, 'line', source, {
            rows: rows(W('renewal_rates'))
              .filter((r) => text(r.subscription_period_number) === period)
              .map(renewal),
            series: [
              series('actual', 'True renewal rate', '#4e79a7', 'percent'),
              series('estimated', 'Estimated renewal rate', '#f28e2b', 'percent', { dashed: true }),
            ],
            span: 'half',
            facet: 'Subscription period ' + period,
            originalSheets: ['Renewal Rate (2)'],
          }),
        );
      }
      board.panels.push(
        panel('renewal-weekly', 'Renewals by week', 'table', source, {
          rows: rows(W('renewal_weekly')).map(renewal),
          columns: columns([
            ['date', 'Week commencing'],
            ['renewed', 'Renewed', 'number'],
            ['decided', 'Decided', 'number'],
            ['actual', 'True rate', 'percent'],
            ['estimated', 'Estimated rate', 'percent'],
          ]),
          span: 'third',
          originalSheets: ['Renewals by Week'],
        }),
        panel('renewal-table', 'Renewal status detail', 'table', source, {
          rows: rows(W('renewal_detail')).map((r) => ({
            ...renewal(r),
            market: r.parent_membership_country,
            period: r.subscription_period_number,
            status: r.renewal_status,
            subscriptions: r.parent_subscriptions,
          })),
          columns: columns([
            ['date', 'Expiry date'],
            ['market', 'Country'],
            ['period', 'Period', 'number'],
            ['status', 'Renewal status'],
            ['subscriptions', 'Parent subscriptions', 'number'],
            ['actual', 'True rate', 'percent'],
          ]),
          span: 'half',
          originalSheets: ['Renewals Tables'],
        }),
      );
    } else if (view === 'cancellations') {
      board.panels.push(
        mPanel('cancellation-kpis', 'Auto-renewal cancellations', source, [
          metric('count', 'Auto-Renewal Cancellations', s.cancellation_rows),
          metric('savings', 'Average LP+ Savings', s.average_savings, 'money'),
          metric(
            'days',
            'Days Between Sign-Up and Cancellation',
            s.average_cancel_days,
            'decimal',
            'Row-weighted AVG of existing customer FIXED MAX days; computed before selected dates.',
          ),
        ]),
      );
      const band =
        filters.bracketMetric === 'TTV'
          ? 'ttv_band'
          : filters.bracketMetric === 'Savings'
            ? 'savings_band'
            : 'margin_band';
      for (const [id, title, card, x, value] of [
        ['cancellations-trend', 'Auto-renewal cancellations', W('cancellation_trend'), 'bucket', 'cancellation_rows'],
        [
          'cancellation-savings',
          (filters.bracketMetric ?? 'Margin') + ' brackets',
          'cancellations_' + band,
          band,
          'customers',
        ],
        ['cancellation-days', 'Days between signup and cancellation', W('cancellation_days'), 'days_band', 'customers'],
      ]) {
        const grouped = pivot(rows(card), x, 'citi_promo', value);
        if (x !== 'bucket') {
          grouped.rows.sort((a, b) => bandSort(text(a.label), text(b.label)));
        }
        board.panels.push(
          panel(id, title, x === 'bucket' ? 'line' : 'bar', source, {
            rows: grouped.rows,
            series: grouped.names.map((name, i) => series('s' + i, name, name === 'Organic' ? '#1f77b4' : '#d62728')),
            stacked: id === 'cancellation-savings',
            span: 'third',
            note:
              id === 'cancellation-savings'
                ? 'Independent source COUNTD customers at each value bracket/promo grain; no grouped distinct addition.'
                : undefined,
          }),
        );
      }
      board.panels.push(
        panel('cancellation-table', 'Cancellation detail by date, country and status', 'table', source, {
          rows: rows(W('cancellation_detail')).map((r) => ({
            ...r,
            market: countryLabel(text(r.country_code_2)),
            status: r.luxplus_status,
            promo: r.citi_promo,
            count: r.cancellation_rows,
            days: ratio(r.days_sum, r.days_count),
            savings: ratio(r.savings_sum, r.savings_count),
          })),
          columns: columns([
            ['date', 'Cancellation date'],
            ['market', 'Country'],
            ['status', 'LuxPlus status'],
            ['promo', 'Promo group'],
            ['count', 'Records', 'number'],
            ['customers', 'Distinct customers', 'number'],
            ['days', 'Average days', 'decimal'],
            ['savings', 'Average savings', 'money'],
          ]),
          originalSheets: ['Cancellations Table'],
        }),
      );
      board.missingFeatures.push(
        'Original cancellation order_currency is absent from luxplus_status_cancellations_latest; authoritative field/join required. Latest state is not a full event history.',
      );
    } else {
      const sourceRows = rows(W('cohort_cells')),
        cohorts = [...new Set(sourceRows.map((r) => text(r.date)))].sort();
      for (const [key, title, format] of [
        ['count_of_sessions', 'Sessions per customer', 'decimal'],
        ['margin_aud', 'Margin per customer', 'money'],
        ['gross_sales_aud', 'Gross sales per customer', 'money'],
        ['count_of_orders', 'Orders per customer', 'decimal'],
      ] as Array<[string, string, MetricFormat]>) {
        const cumulative = new Map<string, FidelityRow>();
        for (const cohort of cohorts) {
          for (const comparison of ['Current', 'TTLY']) {
            const cells = sourceRows
              .filter((r) => text(r.date) === cohort)
              .sort((a, b) => Number(a.month_offset) - Number(b.month_offset));
            const row: FidelityRow = { label: cohort.slice(0, 7) + ' · ' + comparison, cohort, comparison };
            let total: number | null = null;
            for (const cell of cells) {
              const offset = Number(cell.month_offset);
              if (!Number.isInteger(offset) || offset < 1 || offset > 11) {
                throw new Error('Invalid cohort month offset.');
              }
              const n = number(cell[key + (comparison === 'TTLY' ? '_ttly' : '')]);
              if (n !== null) {
                total = (total ?? 0) + n;
              }
              row['m' + offset] = n === null ? null : total;
            }
            cumulative.set(cohort + comparison, row);
          }
        }
        board.panels.push(
          panel(
            {
              count_of_sessions: 'sessionsCumulative',
              margin_aud: 'marginCumulative',
              gross_sales_aud: 'grossSalesCumulative',
              count_of_orders: 'ordersCumulative',
            }[key],
            title,
            'heatmap',
            source,
            {
              rows: [...cumulative.values()],
              columns: [
                { key: 'label', label: 'Enrollment cohort / measure' },
                ...Array.from({ length: 11 }, (_, i) => ({ key: 'm' + (i + 1), label: String(i + 1), format })),
              ],
              series: Array.from({ length: 11 }, (_, i) =>
                series('m' + (i + 1), 'Month ' + (i + 1), '#4e79a7', format),
              ),
              span: 'half',
              note: 'Source AVG per enrollment cohort/month, then cumulative SUM within that cohort. Current and TTLY separate. Exact original colour stops/bounds remain unavailable.',
            },
          ),
        );
      }
      board.missingFeatures.push(
        'No country dimension. Original palette, colour stops and Current/TTLY bounds unavailable.',
      );
    }
    // Preserve complete source breakdown/customer/bracket affordances without generic chart templates.
    const detailIds =
      view.startsWith('takeup') || view === 'margin'
        ? ['opportunities_breakdown']
        : view === 'renewals'
          ? ['renewals_savings_band', 'renewals_margin_band', 'renewals_ttv_band', 'renewals_customers']
          : view === 'cancellations'
            ? [
                'cancellations_savings_band',
                'cancellations_margin_band',
                'cancellations_ttv_band',
                'cancellations_customers',
              ]
            : [];
    for (const id of detailIds) {
      const schema = schemas.find((c) => c.id === id);
      board.panels.push(
        panel('source-' + id, id.replace(/_/g, ' '), 'table', source, {
          rows: rows(id),
          columns: [
            ...schema.keys.map((key) => ({ key, label: key.replace(/_/g, ' ') })),
            ...schema.metrics.map((key) => ({
              key,
              label: key.replace(/_/g, ' '),
              format:
                key.includes('margin') || key.includes('savings') || key.includes('fees') || key.includes('ttv')
                  ? 'money'
                  : 'number',
            })),
          ],
          note: 'Complete independent source projection; scope and linked actions use recovered source bindings.',
        }),
      );
    }
    // Source cards are ordered for transport/aggregation, not necessarily by their date bucket.
    for (const panel of board.panels) {
      if (!['line', 'bar', 'composed'].includes(panel.kind)) continue;
      const dateKey = (row: FidelityRow) =>
        typeof row.date === 'string' && /^\d{4}-\d{2}(?:-\d{2})?$/.test(row.date) ? row.date : null;
      if (!panel.rows.some((row) => dateKey(row) !== null)) continue;
      panel.rows = [...panel.rows].sort((a, b) => {
        const left = dateKey(a),
          right = dateKey(b);
        return left === null ? (right === null ? 0 : 1) : right === null ? -1 : left.localeCompare(right);
      });
    }
    board.originalSheets = [...new Set(board.panels.flatMap((p) => p.originalSheets ?? []))];
    result.ready = true;
  } catch (e) {
    result.problem = e instanceof Error ? e.message : 'Invalid live card population.';
    board.panels = [];
  }
  return result;
}
export function luxPanelCsv(model: SettledLux, panel: FidelityPanel, currentIdentity: string): string {
  if (!model.ready || currentIdentity !== model.identity || !model.board.panels.includes(panel)) {
    throw new Error('LuxPlus scope is not ready for export.');
  }
  const columns = panel.columns.length
    ? panel.columns
    : [
        { key: panel.rows.some((r) => r.date) ? 'date' : 'label', label: 'Date / group' },
        ...panel.series.map((s) => ({ key: s.key, label: s.label })),
      ];
  return (
    '\uFEFF' +
    [
      ...luxCsvScopeRows(model, panel),
      [],
      columns.map((c) => c.label),
      ...panel.rows.map((r) => columns.map((c) => r[c.key])),
    ]
      .map((row) => row.map((v) => csvCell(v, 'Unknown')).join(','))
      .join('\r\n')
  );
}

/** Selected window, falling back to the dates present in the exported rows when the default window is in use. */
function luxCsvWindow(filters: FidelityFilters, panel: FidelityPanel): [string, string] {
  const dates = panel.rows
    .map((r) => text(r.date))
    .filter(Boolean)
    .sort();
  return [filters.startDate || dates[0] || '', filters.endDate || dates[dates.length - 1] || ''];
}

/** Readable key/value scope rows: the report, view, window, country and every non-default filter. No "As of" row
 * is written because the source refresh time isn't available to the page. */
export function luxCsvScopeRows(model: Pick<SettledLux, 'board' | 'view' | 'filters'>, panel: FidelityPanel) {
  const [from, to] = luxCsvWindow(model.filters, panel);
  const extra = Object.entries(model.filters)
    .filter(([key, value]) => !['market', 'startDate', 'endDate'].includes(key) && value && value !== 'All')
    .map(([key, value]) => [key, value]);
  return [
    ['Report', 'LuxPlus'],
    ['View', model.view],
    ['Panel', panel.title],
    ['From', from],
    ['To', to],
    ['Country', model.filters.market || 'All markets'],
    ...extra,
    ['Source', model.board.source],
  ];
}

/** e.g. luxplus-overview-country-2026-09-23-2026-10-20-australia.csv */
export function luxCsvFileName(filters: FidelityFilters, view: FidelityViewId, panel: FidelityPanel): string {
  const [from, to] = luxCsvWindow(filters, panel);
  const country = filters.market && filters.market !== 'All markets' ? filters.market : '';
  return (
    ['luxplus', view, panel.id, from, to, country]
      .filter(Boolean)
      .join('-')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-+/g, '-') + '.csv'
  );
}
